import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, AlertCircle, CheckCircle2 } from "lucide-react";

const SAMPLE = `name,email,password,role,site,department
Jane Doe,jane.doe@esusd.org,Temp@123,staff,LVHS,IT
John Smith,john.smith@esusd.org,Temp@123,staff,BES,Custodial
Mary Roe,mary.roe@esusd.org,,user,CHS,
Sam Lee,sam.lee@esusd.org,,staff,District,Maintenance`;

const HEADERS_HINT = "Required columns: name, email · Optional: password, role, site, department";

export default function BulkImportDialog({ open, onOpenChange, onImported }) {
  const [csv, setCsv] = useState(SAMPLE);
  const [defaultPassword, setDefaultPassword] = useState("Welcome@123");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!csv.trim()) { toast.error("Paste some CSV first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/users/bulk", { csv, default_password: defaultPassword });
      setResult(data);
      const parts = [];
      if (data.created_count) parts.push(`${data.created_count} created`);
      if (data.updated_count) parts.push(`${data.updated_count} updated`);
      if (data.error_count) parts.push(`${data.error_count} skipped`);
      toast.success(parts.join(" · ") || "Nothing to import");
      onImported?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "esusd_staff_import_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white" data-testid="bulk-import-dialog">
        <DialogHeader>
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-500">Administration</div>
          <DialogTitle className="font-heading text-2xl text-zinc-950">Bulk import staff</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Paste CSV rows below. Existing emails are updated; new ones are created. {HEADERS_HINT}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">CSV</div>
            <button
              type="button"
              onClick={downloadTemplate}
              className="text-xs text-[#0055FF] hover:text-[#0040CC] inline-flex items-center gap-1"
              data-testid="bulk-download-template"
            >
              <Download className="h-3 w-3" /> Download template
            </button>
          </div>
          <Textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            rows={10}
            spellCheck={false}
            data-testid="bulk-csv-input"
            className="font-mono text-xs rounded-md focus-visible:ring-2 focus-visible:ring-blue-500/20"
          />

          <div className="grid grid-cols-2 gap-4 items-end">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Default password</Label>
              <Input
                value={defaultPassword}
                onChange={(e) => setDefaultPassword(e.target.value)}
                placeholder="Applied to rows without a password"
                data-testid="bulk-default-password"
                className="h-10 rounded-md"
              />
            </div>
            <div className="text-xs text-zinc-500 leading-relaxed">
              <div className="font-medium text-zinc-700">Site codes:</div>
              District · LVHS · LVES · BES · EBES · CHS · AES
            </div>
          </div>

          {result && (
            <div className="rounded-md border border-zinc-200 divide-y divide-zinc-200 text-sm">
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700">
                <CheckCircle2 className="h-4 w-4" />
                <span><strong>{result.created_count}</strong> created · <strong>{result.updated_count}</strong> updated</span>
              </div>
              {result.errors?.length > 0 && (
                <div className="px-4 py-3">
                  <div className="flex items-center gap-2 text-red-600 mb-2">
                    <AlertCircle className="h-4 w-4" />
                    <span className="font-medium">{result.error_count} row(s) skipped</span>
                  </div>
                  <ul className="text-xs text-zinc-700 space-y-1 max-h-32 overflow-y-auto">
                    {result.errors.map((e, i) => (
                      <li key={`err-${e.row}-${i}`}><span className="font-mono text-zinc-500">row {e.row}</span> — {e.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-cancel-button">Close</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="bulk-import-submit"
            className="bg-[#0055FF] hover:bg-[#0040CC] text-white transition-colors duration-200"
          >
            <Upload className="h-4 w-4 mr-2" />
            {busy ? "Importing…" : "Import CSV"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
